import { useQuery } from '@tanstack/react-query';
import * as _ from 'lodash';
import type { QueryKey } from '@tanstack/react-query';

import { get } from '../../../redux/api';
import type { LicenseSummary } from '../../../../shared/types/licenses';
import type { License } from '../../../types';

//  We can keep this data around for a bit longer since license data basically never changes.
const STALE_TIME = 1000 * 60 * 10; // 10 minutes

type LicenseResponse = License & {
  Summary?: LicenseSummary;
};

type LicenseIdOnly = {
  licenseId: string;
};

export type UseLicensesParams = {
  title?: string;
  pageSize: number;
};

/**
 * Takes a license response and slims it down to the LicenseData type.
 * Will try to use the included `Summary` if available, otherwise falls back to `licenseComments`.
 */
function formatLicense({ licenseId }: LicenseResponse): LicenseIdOnly {
  return {
    licenseId,
  };
}

/**
 * Async function to fetch data about all licenses, format it, and return it.
 *
 * @param onlyIds Whether to only fetch the IDs of the licenses.
 * @param params The parameters for fetching licenses.
 * @returns The formatted license data.
 */
async function getLicenses<T extends boolean>(
  onlyIds: T,
  params: UseLicensesParams,
): Promise<T extends true ? LicenseIdOnly[] : LicenseResponse[]> {
  // Replace title with $title since that's what the backend expects.
  const queryParams = {
    ..._.omit(params, 'title'),
    $title: params.title,
  };
  const { data } = await get<LicenseResponse[]>('/api/licenses', false, queryParams);
  if (!data) {
    throw new Error(`Licenses could not be fetched!`);
  }

  if (onlyIds) {
    return data.map(summary => formatLicense(summary)) as T extends true ? LicenseIdOnly[] : LicenseResponse[];
  }
  return data as T extends true ? LicenseIdOnly[] : LicenseResponse[];
}

const getQueryKey = (onlyIds: boolean, params: UseLicensesParams): QueryKey => ['license', onlyIds, params];

/**
 * Hook which will fetch data about all licenses.
 * Caches the license data for 10 minutes, since it's unlikely to ever change.
 *
 * @param onlyIds Whether to only fetch the IDs of the licenses.
 * @param params The parameters for fetching licenses.
 */
export default function useLicenses<T extends boolean>(
  onlyIds: T,
  params: UseLicensesParams,
): ReturnType<typeof useQuery<T extends true ? LicenseIdOnly[] : LicenseResponse[]>> {
  return useQuery<T extends true ? LicenseIdOnly[] : LicenseResponse[]>({
    queryKey: getQueryKey(onlyIds, params),
    queryFn: () => getLicenses(onlyIds, params),
    staleTime: STALE_TIME,
  });
}
